import React, { useState, useMemo, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemText,
    Tabs,
    Tab,
    Fab,
    Snackbar,
    Alert,
    Collapse,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    History as HistoryIcon,
    Add as AddIcon,
    Public as PublicIcon,
    WhatsApp as WhatsAppIcon,
    ExpandMore as ExpandMoreIcon,
    ChevronRight as ChevronRightIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    Receipt as ReceiptIcon,
    SwapHoriz as SwapHorizIcon,
    AttachMoney as AttachMoneyIcon,
    Description as DescriptionIcon,
} from '@mui/icons-material';
import { useServiceOrders, usePaginatedServiceOrders } from '../../../hooks/administration/useServiceOrders';
import ServiceOrderForm from './ServiceOrderForm';
import { useNavigate } from "react-router-dom";
import { useAuthContext } from '../../../context/AuthProvider';

const ESTADO_COLOR_MAP = {
    CREADO: { label: "CREADO", color: "#90a4ae", text: "#fff" },
    PLANIFICADO: { label: "PLANIFICADO", color: "#ffb300", text: "#000" },
    EN_PROCESO: { label: "EN PROCESO", color: "#4caf50", text: "#fff" },
    COMPLETADO: { label: "COMPLETADO", color: "#1e88e5", text: "#fff" },
    CANCELADO: { label: "CANCELADO", color: "#e53935", text: "#fff" },
};

// Configuración de estados y colores
const ESTADO_CONFIG = {
    CREADO: { label: "CREADO", color: "#90a4ae", text: "#fff" },
    VALIDADA: { label: "VALIDADA", color: "#fbc02d", text: "#000" },
    LIBERADA: { label: "LIBERADA", color: "#4caf50", text: "#fff" },
    EN_EJECUCION: { label: "EN EJECUCIÓN", color: "#1976d2", text: "#fff" },
    FINALIZADA: { label: "FINALIZADA", color: "#388e3c", text: "#fff" },
    CERRADA: { label: "CERRADA", color: "#00695c", text: "#fff" },
    CANCELADA: { label: "CANCELADA", color: "#d32f2f", text: "#fff" },
};

const TAB_CONFIG = [
    { label: "CREADOS", filter: (servicio) => servicio.ordsercabest === 'CREADO' },
    { label: "VALIDADAS", filter: (servicio) => servicio.ordsercabest === 'VALIDADA' },
    { label: "LIBERADAS", filter: (servicio) => servicio.ordsercabest === 'LIBERADA' },
    { label: "EN EJECUCIÓN", filter: (servicio) => servicio.ordsercabest === 'EN_EJECUCION' },
    { label: "FINALIZADAS", filter: (servicio) => servicio.ordsercabest === 'FINALIZADA' },
    { label: "CERRADAS", filter: (servicio) => servicio.ordsercabest === 'CERRADA' },
    { label: "CANCELADAS", filter: (servicio) => servicio.ordsercabest === 'CANCELADA' }
];

const ServiceOrdersDashboard = () => {
    // Hooks y contexto
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const {
        //serviceOrders,
        //error,
        //isLoading,
        createMutation,
        updateMutation,
        deleteMutation,
        updateManifestMutation,
        useFetchServiceManifests
    } = useServiceOrders();

    // Estados del componente
    const [openForm, setOpenForm] = useState(false);
    const [editOrder, setEditOrder] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    // Filtros y paginación
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [search, setSearch] = useState("");
    const [codigoFilter, setCodigoFilter] = useState("");
    const [currentTab, setCurrentTab] = useState(0);

    const {
        serviceOrders: paginatedServiceOrders,
        //serviceOrders,
        total,
        error,
        isLoading
    } = usePaginatedServiceOrders({ page: page + 1, limit: rowsPerPage });

    // Filas expandibles
    const [expandedRows, setExpandedRows] = useState({});

    // Nuevos estados para la edición de manifiestos asociados
    const [openManifestEdit, setOpenManifestEdit] = useState(false);
    const [currentManifest, setCurrentManifest] = useState(null);
    const [manifestForm, setManifestForm] = useState({
        ordSerManObs: '',
        ordSerManPre: 0,
        ordSerManPrePro: 0
    });

    // IDs de roles
    const administradorID = Number(process.env.REACT_APP_ROL_ADMINISTRADOR_ID);
    const operacionesID = Number(process.env.REACT_APP_ROL_OPERACIONES_ID);
    const gerenciaID = Number(process.env.REACT_APP_ROL_GERENTE_ID);
    const adminID = Number(process.env.REACT_APP_ROL_ADMIN_ID);

    // Funciones de utilidad
    const showAlert = useCallback((message, severity = "success") => {
        setAlert({ open: true, message, severity });
        setTimeout(() => setAlert(prev => ({ ...prev, open: false })), 5000);
    }, []);

    const formatFechaLarga = useCallback((dateString) => {
        if (!dateString) return '';
        const fecha = new Date(dateString + 'T00:00:00');
        return new Intl.DateTimeFormat('es-PE', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        }).format(fecha);
    }, []);

    // Filtrado y ordenamiento de datos  
    const displayedServices = useMemo(() => {
        // Aquí puedes aplicar filtros adicionales si es necesario, pero la paginación ya viene hecha
        if (!paginatedServiceOrders) return [];

        return paginatedServiceOrders.filter(servicio => {
            const codigoCompleto = `OS-${String(servicio.ordsercabid).padStart(6, "0")}`.toLowerCase();
            const codigoRaw = String(servicio.ordsercabid);

            const matchesSearch = search === "" ||
                servicio.ordsercabnom.toLowerCase().includes(search.toLowerCase()) ||
                servicio.pronom.toLowerCase().includes(search.toLowerCase()) ||
                servicio.serpronom.toLowerCase().includes(search.toLowerCase());

            const matchesCodigo = codigoFilter === "" ||
                codigoCompleto.includes(codigoFilter.toLowerCase()) ||
                codigoRaw.includes(codigoFilter.replace(/^os-0+/i, ""));

            return matchesSearch && matchesCodigo && TAB_CONFIG[currentTab].filter(servicio);
        });
    }, [paginatedServiceOrders, search, codigoFilter, currentTab]);

    // Handlers
    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
        setPage(0);
    };

    const handleToggleExpand = useCallback((ordSerCabID) => {
        setExpandedRows(prev => ({
            ...prev,
            [ordSerCabID]: !prev[ordSerCabID]
        }));
    }, []);

    const handleSubmit = useCallback((data) => {
        if (!user) {
            showAlert("Debe iniciar sesión", "error");
            return;
        }

        const operation = editOrder
            ? updateMutation.mutate(
                { id: data.ordsercabid, data: { ...data, updbyid: user.EmpID } },
                {
                    onSuccess: () => {
                        showAlert("Orden actualizada correctamente");
                        setOpenForm(false);
                        setEditOrder(null);
                    },
                    onError: () => showAlert("Error al actualizar la orden", "error"),
                }
            )
            : createMutation.mutate(
                { ...data, crebyid: user.EmpID },
                {
                    onSuccess: () => {
                        showAlert("Orden creada con éxito");
                        setOpenForm(false);
                    },
                    onError: () => showAlert("Error al crear la orden", "error"),
                }
            );

        return operation;
    }, [editOrder, user, showAlert, updateMutation, createMutation]);

    const handleConfirmDelete = useCallback(() => {
        if (!selectedOrder) return;

        deleteMutation.mutate(selectedOrder.ordsercabid, {
            onSuccess: () => {
                showAlert("Orden eliminada con éxito");
                setOpenConfirm(false);
                setSelectedOrder(null);
            },
            onError: (error) => {
                showAlert(error.message || "Error al eliminar la orden", "error");
                setOpenConfirm(false);
                setSelectedOrder(null);
            },
        });
    }, [selectedOrder, deleteMutation, showAlert]);

    // Estilos dinámicos para pestañas
    const getTabStyle = (tabIndex) => ({
        backgroundColor: tabIndex === currentTab ?
            `rgba(${parseInt(ESTADO_CONFIG[TAB_CONFIG[tabIndex].filter.toString().split("'")[1]]?.color.slice(1, 3), 16)}, 
       ${parseInt(ESTADO_CONFIG[TAB_CONFIG[tabIndex].filter.toString().split("'")[1]]?.color.slice(3, 5), 16)}, 
       ${parseInt(ESTADO_CONFIG[TAB_CONFIG[tabIndex].filter.toString().split("'")[1]]?.color.slice(5, 7), 16)}, 0.3)` :
            'transparent',
        borderBottom: tabIndex === currentTab ?
            `3px solid ${ESTADO_CONFIG[TAB_CONFIG[tabIndex].filter.toString().split("'")[1]]?.color}` :
            'none'
    });

    // Handler para abrir el formulario de edición de manifiesto
    const handleOpenManifestEdit = (manifest) => {
        setCurrentManifest(manifest);
        setManifestForm({
            ordSerManObs: manifest.ordsermanobs || '',
            ordSerManPre: manifest.ordsermanpre ? parseFloat(manifest.ordsermanpre) : 0,
            ordSerManPrePro: manifest.ordsermanprepro ? parseFloat(manifest.ordsermanprepro) : 0
        });
        setOpenManifestEdit(true);
    };

    // Handler para cerrar el formulario de edición de manifiesto
    const handleCloseManifestEdit = () => {
        setOpenManifestEdit(false);
        setCurrentManifest(null);
        setManifestForm({
            ordSerManObs: '',
            ordSerManPre: 0,
            ordSerManPrePro: 0
        });
    };

    // Handler para cambios en el formulario
    const handleManifestFormChange = (e) => {
        const { name, value } = e.target;
        setManifestForm(prev => ({
            ...prev,
            [name]: name === 'ordSerManPre' ? parseFloat(value) || 0 : value,
            [name]: name === 'ordSerManPrePro' ? parseFloat(value) || 0 : value,
        }));
    };

    // Handler para guardar los cambios del manifiesto
    const handleSaveOrderManifestEdit = async () => {
        const payload = {
            ordsercabid: currentManifest.ordsercabid, // necesario para recalcular OrdSerPre
            manid: currentManifest.manid,
            ordsermanpre: manifestForm.ordSerManPre,
            ordsermanprepro: manifestForm.ordSerManPrePro,
            ordsermanobs: manifestForm.ordSerManObs,
            updbyid: user.EmpID,
        };

        updateManifestMutation.mutate(
            {
                id: currentManifest.ordsermanid,
                data: payload,
            },
            {
                onSuccess: () => {
                    showAlert("Orden-Manifiesto actualizado correctamente");
                    handleCloseManifestEdit();
                },
                onError: () => {
                    showAlert("Error al actualizar la Orden-Manifiesto", "error");
                },
            }
        );
    };

    const tramoMap = {
        IDA: { label: 'SUBIDA', color: '#2e7d32' },   // verde
        VUELTA: { label: 'BAJADA', color: '#1565c0' } // azul
    };

    const ManifestsRow = ({ serviceOrderId, isExpanded, useFetchServiceManifests }) => {
        // Usa el hook que recibes como prop
        const { data: manifiestos_asociados = [], isLoading } = useFetchServiceManifests(isExpanded ? serviceOrderId : null);

        return (
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="subtitle1" gutterBottom component="div">
                                Manifiestos Asociados ({manifiestos_asociados?.length || 0})
                            </Typography>
                            {isLoading ? (
                                <Box display="flex" justifyContent="center" py={2}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                manifiestos_asociados.length > 0 ? (
                                    <Table size="small" aria-label="manifiestos">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>Código</strong></TableCell>
                                                <TableCell><strong>Trayecto</strong></TableCell>
                                                <TableCell><strong>T. Vehiculo</strong></TableCell>
                                                <TableCell><strong>Fecha de Salida</strong></TableCell>
                                                <TableCell><strong>Origen</strong></TableCell>
                                                <TableCell><strong>Destino</strong></TableCell>
                                                <TableCell><strong>Precio Uni.</strong></TableCell>
                                                <TableCell><strong>Estado</strong></TableCell>
                                                <TableCell align="center"><strong>Acciones</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {manifiestos_asociados.map((manifiestoLink) => (
                                                // Corregido: Usa la key de manera segura y consistente
                                                <TableRow key={manifiestoLink.ordsercabid + '-' + manifiestoLink.manid}>
                                                    <TableCell>
                                                        <b>MN-{String(manifiestoLink.manid).padStart(6, "0")}</b>
                                                    </TableCell>
                                                    <TableCell>
                                                        {tramoMap[manifiestoLink.ordsermantra] ? (
                                                            <Chip
                                                                label={tramoMap[manifiestoLink.ordsermantra].label}
                                                                variant="outlined"
                                                                size="small"
                                                                sx={{
                                                                    borderWidth: 2,
                                                                    borderColor: tramoMap[manifiestoLink.ordsermantra].color,
                                                                    color: tramoMap[manifiestoLink.ordsermantra].color,
                                                                    fontWeight: 'bold',
                                                                    letterSpacing: '0.5px'
                                                                }}
                                                            />
                                                        ) : (
                                                            manifiestoLink.ordsermantra
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{manifiestoLink.vehdes}</TableCell>
                                                    <TableCell>
                                                        {/*manifiestoLink.manfec*/}
                                                        {formatFechaLarga(manifiestoLink.manfec)}
                                                    </TableCell>
                                                    <TableCell>{manifiestoLink.manori}</TableCell>
                                                    <TableCell>{manifiestoLink.mandes}</TableCell>
                                                    <TableCell>S/. {manifiestoLink.ordsermanpre}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={ESTADO_COLOR_MAP[manifiestoLink.manest]?.label || manifiestoLink.manest}
                                                            sx={{
                                                                backgroundColor: ESTADO_COLOR_MAP[manifiestoLink.manest]?.color || '#ccc',
                                                                color: ESTADO_COLOR_MAP[manifiestoLink.manest]?.text || '#000',
                                                                fontWeight: 'bold'
                                                            }}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {/* ... Botones de acción ... */}
                                                        {/*['COMPLETADO', 'CANCELADO'].includes(manifiestoLink.manest) && (
                                                            <Tooltip title="Editar">
                                                                <IconButton
                                                                    onClick={() => handleOpenManifestEdit(manifiestoLink)}
                                                                    size="small"
                                                                >
                                                                    <EditIcon color="primary" fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )*/}
                                                        <Tooltip title="Editar">
                                                            <IconButton
                                                                onClick={() => handleOpenManifestEdit(manifiestoLink)}
                                                                size="small"
                                                            >
                                                                <EditIcon color="primary" fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Ver Manifiesto">
                                                            <IconButton
                                                                onClick={() => navigate(`/administracion/manifiestos/${manifiestoLink.manid}/pasajeros`)}
                                                                size="small"
                                                            >
                                                                <VisibilityIcon sx={{ color: "#009688" }} fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mb: 1 }}>
                                        No hay manifiestos asociados a esta orden.
                                    </Typography>
                                )
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        );
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ my: 3 }}>
                Error al cargar órdenes de servicio: {error.message}
            </Alert>
        );
    }

    return (
        <Paper sx={{ p: 3, backgroundColor: "#fafafa", borderRadius: 2 }}>
            {/* Alertas */}
            <Snackbar
                open={alert.open}
                autoHideDuration={5000}
                onClose={() => setAlert(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    severity={alert.severity}
                    onClose={() => setAlert(prev => ({ ...prev, open: false }))}
                >
                    {alert.message}
                </Alert>
            </Snackbar>

            {/* Encabezado */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" fontWeight="bold" color="primary">
                    Gestión de Órdenes de Servicio
                </Typography>
                {[administradorID].includes(user?.RolID) && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setEditOrder(null);
                            setOpenForm(true);
                        }}
                        sx={{ textTransform: 'none' }}
                    >
                        Nueva Orden
                    </Button>
                )}
            </Box>

            {/* Filtros */}
            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                <TextField
                    label="Buscar por nombre/proyecto/tipo"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: 2, minWidth: 250 }}
                />
                <TextField
                    label="Buscar por código (OS-000123 o 123)"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={codigoFilter}
                    onChange={(e) => setCodigoFilter(e.target.value)}
                    sx={{ flex: 1, minWidth: 250 }}
                />
            </Box>

            {/* Pestañas */}
            <Tabs
                value={currentTab}
                onChange={handleTabChange}
                variant="fullWidth"
                scrollButtons="auto"
                sx={{
                    mb: 3,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    '& .MuiTabs-indicator': { display: 'none' },
                    '& .MuiTab-root': {
                        minHeight: 48,
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        color: 'text.secondary',
                        '&.Mui-selected': {
                            color: "#000",
                        },
                    },
                }}
            >
                {TAB_CONFIG.map((tab, index) => (
                    <Tab
                        key={index}
                        label={tab.label}
                        sx={getTabStyle(index)}
                    />
                ))}
            </Tabs>

            {/* Tabla de resultados */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                <Table>
                    <TableHead sx={{ bgcolor: 'background.default' }}>
                        <TableRow>
                            <TableCell><strong>Código</strong></TableCell>
                            <TableCell><strong>Proyecto</strong></TableCell>
                            <TableCell><strong>Tipo Servicio</strong></TableCell>
                            <TableCell><strong>Nombre</strong></TableCell>
                            <TableCell><strong>Fecha</strong></TableCell>
                            {[adminID, administradorID, gerenciaID].includes(user?.RolID) && (
                                <TableCell><strong>Precio Tot.</strong></TableCell>
                            )}
                            <TableCell><strong>Estado</strong></TableCell>
                            <TableCell align="center"><strong>Acciones</strong></TableCell>
                            <TableCell width="50px"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayedServices.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                    <Typography variant="subtitle1" color="text.secondary">
                                        No se encontraron órdenes para esta selección
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            displayedServices.map((servicio) => (
                                <React.Fragment key={servicio.ordsercabid}>
                                    <TableRow
                                        hover
                                        sx={(theme) => ({
                                            backgroundColor: servicio.serproint
                                                ? theme.palette.primary.light + '22' // 22 = ~13% opacidad
                                                : 'inherit',
                                            borderLeft: servicio.serproint
                                                ? `4px solid ${theme.palette.primary.main}`
                                                : '4px solid transparent',
                                        })}
                                    >
                                        <TableCell>
                                            <Typography fontWeight="bold" color="primary">
                                                OS-{String(servicio.ordsercabid).padStart(6, "0")}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ maxWidth: 200 }}>
                                            <Typography noWrap title={servicio.pronom}>
                                                {servicio.pronom}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>{servicio.serpronom}</TableCell>

                                        <TableCell sx={{ maxWidth: 200 }}>
                                            <Typography noWrap title={servicio.ordsercabnom}>
                                                {servicio.ordsercabnom}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>{formatFechaLarga(servicio.ordsercabfec)}</TableCell>

                                        {[adminID, administradorID, gerenciaID].includes(user?.RolID) && (
                                            <TableCell>S/. {servicio.ordsercabpre}</TableCell>
                                        )}

                                        <TableCell>
                                            <Chip
                                                label={ESTADO_CONFIG[servicio.ordsercabest]?.label || servicio.ordsercabest}
                                                sx={{
                                                    backgroundColor: ESTADO_CONFIG[servicio.ordsercabest]?.color || '#ccc',
                                                    color: ESTADO_CONFIG[servicio.ordsercabest]?.text || '#000',
                                                    fontWeight: 'bold',
                                                    minWidth: 120
                                                }}
                                                size="small"
                                            />
                                        </TableCell>

                                        <TableCell align="center" sx={{ minWidth: 130 }}>
                                            <Box display="flex" justifyContent="center" gap={1}>
                                                {[administradorID].includes(user?.RolID) && (
                                                    <Tooltip title="Editar">
                                                        <IconButton
                                                            onClick={() => {
                                                                setEditOrder(servicio);
                                                                setOpenForm(true);
                                                            }}
                                                            size="small"
                                                        >
                                                            <EditIcon color="primary" fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {/*<Tooltip title="Ver detalles">
                                                    <IconButton
                                                        onClick={() => navigate(`/administracion/servicio/${servicio.ordsercabid}/viajeros`)}
                                                        size="small"
                                                    >
                                                        <VisibilityIcon sx={{ color: "#009688" }} fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>*/}

                                                <Tooltip title="Ver detalles">
                                                    <IconButton
                                                        onClick={() => navigate(`/administracion/servicio2/${servicio.ordsercabid}/viajeros`)}
                                                        size="small"
                                                    >
                                                        <VisibilityIcon sx={{ color: "#009688" }} fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                {[gerenciaID, adminID].includes(user?.RolID) && (
                                                    <Tooltip title="Ver historial">
                                                        <IconButton
                                                            onClick={() => navigate(`/administracion/servicio/${servicio.ordsercabid}/historial`)}
                                                            size="small"
                                                        >
                                                            <HistoryIcon color="secondary" fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {user?.RolID === adminID && (
                                                    <Tooltip title="Eliminar">
                                                        <IconButton
                                                            onClick={() => {
                                                                setSelectedOrder(servicio);
                                                                setOpenConfirm(true);
                                                            }}
                                                            size="small"
                                                        >
                                                            <DeleteIcon color="error" fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        {[adminID, administradorID, gerenciaID].includes(user?.RolID) && (
                                            <TableCell align="right">
                                                <IconButton
                                                    aria-label="expand row"
                                                    size="small"
                                                    onClick={() => handleToggleExpand(servicio.ordsercabid)}
                                                >
                                                    {expandedRows[servicio.ordsercabid] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                    <ManifestsRow
                                        serviceOrderId={servicio.ordsercabid}
                                        isExpanded={expandedRows[servicio.ordsercabid]}
                                        useFetchServiceManifests={useFetchServiceManifests} // <-- Pasamos la función como prop
                                    />
                                </React.Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Paginación */}
            <TablePagination
                rowsPerPageOptions={[25, 50, 100, 200]}
                component="div"
                count={total}
                //count={displayedServices.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 100));
                    setPage(0);
                }}
                sx={{ mt: 2 }}
            />

            {/* Modales */}
            <ServiceOrderForm
                open={openForm}
                handleClose={() => {
                    setOpenForm(false);
                    setEditOrder(null);
                }}
                onSubmit={handleSubmit}
                defaultValues={editOrder || {}}
                isLoading={createMutation.isLoading || updateMutation.isLoading}
            />

            <Dialog
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de eliminar la orden <strong>{selectedOrder?.ordsercabnom}</strong>?
                        <br />
                        <Typography variant="body2" color="error" mt={1}>
                            Esta acción no se puede deshacer.
                        </Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)} color="primary">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleteMutation.isLoading}
                        startIcon={deleteMutation.isLoading ? <CircularProgress size={20} /> : null}
                    >
                        {deleteMutation.isLoading ? "Eliminando..." : "Eliminar"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de edición de manifiesto asociado */}            
            <Dialog
                open={openManifestEdit}
                onClose={handleCloseManifestEdit}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            <ReceiptIcon color="primary" />
                            <Typography variant="h6" component="span">
                                Editar Manifiesto Asociado
                            </Typography>
                        </Box>
                        <IconButton onClick={handleCloseManifestEdit} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {currentManifest && (
                        <Box sx={{ mt: 1 }}>
                            {/* === SECCIÓN INFORMATIVA === */}
                            <Box display="flex" gap={2} flexWrap="wrap">
                                <TextField
                                    label="Código de Manifiesto"
                                    value={`MN-${String(currentManifest.manid).padStart(6, "0")}`}
                                    fullWidth
                                    margin="normal"
                                    InputProps={{
                                        readOnly: true,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <ReceiptIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#f5f5f5',
                                        },
                                    }}
                                />

                                <TextField
                                    label="Trayecto"
                                    value={currentManifest.ordsermantra === "IDA" ? "SUBIDA" : "BAJADA"}
                                    fullWidth
                                    margin="normal"
                                    InputProps={{
                                        readOnly: true,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SwapHorizIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#f5f5f5',
                                        },
                                    }}
                                />
                            </Box>

                            <Divider sx={{ my: 2 }}>
                                <Chip label="Precios" size="small" icon={<AttachMoneyIcon />} />
                            </Divider>

                            {/* === PRECIO AL CLIENTE === */}
                            <TextField
                                label="Precio al cliente"
                                name="ordSerManPre"
                                type="number"
                                value={manifestForm.ordSerManPre}
                                onChange={handleManifestFormChange}
                                onFocus={(e) => e.target.select()}   // <--- NUEVO
                                fullWidth
                                margin="normal"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography variant="body2" color="text.secondary">
                                                S/
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{
                                    step: "0.01",
                                    min: "0",
                                }}
                                helperText="Monto que se cobra al pasajero por este viaje"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#e3f2fd',
                                        '& fieldset': {
                                            borderColor: '#1976d2',
                                            borderWidth: 2,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#1565c0',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#0d47a1',
                                        fontWeight: 500,
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#0d47a1',
                                    },
                                }}
                            />

                            {/* === PRECIO AL PROVEEDOR === */}
                            <TextField
                                label="Precio del proveedor (taxista)"
                                name="ordSerManPrePro"
                                type="number"
                                value={manifestForm.ordSerManPrePro}
                                onChange={handleManifestFormChange}
                                onFocus={(e) => e.target.select()}   // <--- NUEVO
                                fullWidth
                                margin="normal"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography variant="body2" color="text.secondary">
                                                S/
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{
                                    step: "0.01",
                                    min: "0",
                                }}
                                helperText="Monto que se paga al taxista o proveedor externo"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#fff3e0',
                                        '& fieldset': {
                                            borderColor: '#ed6c02',
                                            borderWidth: 2,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#e65100',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#bf360c',
                                        fontWeight: 500,
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#bf360c',
                                    },
                                }}
                            />

                            <Divider sx={{ my: 2 }} />

                            {/* === OBSERVACIONES === */}
                            <TextField
                                label="Observaciones"
                                name="ordSerManObs"
                                value={manifestForm.ordSerManObs}
                                onChange={handleManifestFormChange}
                                fullWidth
                                margin="normal"
                                multiline
                                rows={4}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <DescriptionIcon fontSize="small" color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                helperText="Comentarios adicionales (opcional)"
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={handleCloseManifestEdit}
                        variant="outlined"
                        color="secondary"
                        startIcon={<CloseIcon />}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSaveOrderManifestEdit}
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                    >
                        Guardar Cambios
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default ServiceOrdersDashboard;